// src/encounter/lib/to-medication-order.ts
// Map a picked DS MedicationResult + a structured dose capture → a DS PlanOrder
// attached to a Dx + the CodedOrderPayload kept in the app-side side-map (PROD-1167).
//
// PROD-1165's v1 attached a medication with only a display-string label (strength
// legible but unstructured). This produces the STRUCTURED payload — route /
// frequency / duration / sig — so the saved PatientAssessment medication carries
// real fields (MedicationRequest-grade), not just a label. The dialog captures the
// dose; this mapper is the pure transform. No Math.random/Date.now (caller-supplied
// orderId — the same med can attach to >1 Dx, so the id is per-attachment).
import type { MedicationResult, PlanOrder } from '@scripsteam/scrips-signal-ds';
import type { CodedOrderPayload } from './order-payload';

/**
 * The structured dose captured in the dialog (PROD-1340 full parity). route / doseUnit / dispense /
 * dispenseUnit are BE-required (PROD-1339); frequency drives the occurrence inputs; the rest optional.
 */
export interface MedicationDose {
  /**
   * Set when the TRANSCRIPT contradicts the schedule the model produced (PROD-2018). Human-readable
   * and rendered verbatim — never assembled by a model.
   *
   * WHY THIS EXISTS AND WITHHOLDING WAS NOT ENOUGH. The first version of the guard simply dropped
   * `frequency` and `timesADay` when the transcript disagreed. Cross-model review found that makes
   * it WORSE: the dose dialog defaults an absent frequency to `everyday` with `timesADay: 1`, so
   * "250 units twice weekly" went from BID (14 doses/week) to QD (7 doses/week) — still wrong,
   * still silent, and now wearing a completed form. On a false positive it degraded a CORRECT
   * twice-daily schedule to once-daily and changed the derived dispense quantity.
   *
   * A withheld value is indistinguishable from a value nobody dictated. Only a message can carry
   * the difference, so the contradiction travels as text and the dialog must show it.
   */
  contradiction?: string;
  /**
   * The dictated drug NAME this sig was spoken for (`frameV2` only, `dictated-dose-for-pick.ts`):
   * set whenever a dictated sig rides onto the catalogue row the clinician picked. Neither a name
   * match nor a search match is proof of identity ("Paracetamol + Codeine" contains "paracetamol";
   * a brand row such as "ADOL 500MG …" matched only in its description), so the dose form says
   * where the values came from and asks the physician to check them against the product she
   * picked. Display only; never part of an order payload.
   */
  dictatedFor?: string;
  /** Route CODE (from the med's route[]); routeDisplay carries its label. */
  route?: string;
  routeDisplay?: string;
  /** Dose unit (mg/ml/tablet…) → wire `unitOfMeasurement`. */
  doseUnit?: string;
  /**
   * Coded frequency type — the WIRE values: `everyday` / `needed` / `now` / `day_interval` /
   * `specific`, per `MED_FREQUENCY`.
   *
   * This comment previously listed `every_day/as_needed/now/days_interval/specific_days`, which is
   * the exact set PROD-1445 records as mis-parsing in the order-send -> e-prescription path. A
   * stale comment naming the broken spellings is worse than none: it is read as the contract.
   */
  frequency?: string;
  /** Units per intake → occurenceRequest.count. */
  quantity?: number;
  /** Times a day → occurenceRequest.period. */
  timesADay?: number;
  /** Every N days (days_interval). */
  daysInterval?: number;
  /** Weekdays (specific_days). */
  specificDays?: string[];
  /** Start date for scheduled frequencies (ISO, no-Z) → wire `onSetDate`. */
  startDate?: string;
  duration?: number;
  durationUnit?: string;
  /** Dispense quantity (>0). */
  dispense?: number;
  /** Dispense unit. */
  dispenseUnit?: string;
  refill?: number;
  substitution?: boolean;
  sig?: string;
  note?: string;
  /**
   * Drug-interaction alerts for THIS prescription (Flutter `vidalAlertRequest`, passed on save at
   * new_medication_add_update_widget.dart:1350). Opaque — we carry the engine's rows through rather
   * than re-modelling them.
   */
  vidalAlerts?: unknown[];
  /** Patient weight (frequency `now`). */
  weight?: number;
  /** Order priority (frequency `now`). */
  priority?: string;
}

export interface MedicationOrderResult {
  planOrder: PlanOrder;
  payload: Extract<CodedOrderPayload, { kind: 'medication' }>;
}

/** Build the order label: "Name strength · sig" (strength + sig only when present). */
function medicationLabel(m: MedicationResult, sig: string | undefined): string {
  const base = m.strength ? `${m.name} ${m.strength}` : m.name;
  return sig ? `${base} · ${sig}` : base;
}

export function medicationResultToOrder(
  // `system`: the coding system of the picked medication (PROD-2831). Written onto
  // `medicationCode.system` only when present; a blank one stays off the payload (never guessed).
  m: MedicationResult & { system?: string | null },
  dose: MedicationDose,
  orderId: string,
): MedicationOrderResult {
  const system = m.system?.trim() || undefined;
  const route = dose.route?.trim() || undefined;
  const routeDisplay = dose.routeDisplay?.trim() || route;
  const doseUnit = dose.doseUnit?.trim() || undefined;
  const dispenseUnit = dose.dispenseUnit?.trim() || undefined;
  const frequency = dose.frequency?.trim() || undefined;
  const durationUnit = dose.durationUnit?.trim() || undefined;
  const sig = dose.sig?.trim() || undefined;
  const vidalAlerts = dose.vidalAlerts?.length ? dose.vidalAlerts : undefined;
  const note = dose.note?.trim() || undefined;
  const startDate = dose.startDate?.trim() || undefined;
  const priority = dose.priority?.trim() || undefined;
  const specificDays = dose.specificDays?.filter((d) => !!d?.trim());
  const durationValue = typeof dose.duration === 'number' && dose.duration > 0 ? dose.duration : undefined;
  const dispense = typeof dose.dispense === 'number' && dose.dispense > 0 ? dose.dispense : undefined;
  const quantity = typeof dose.quantity === 'number' && dose.quantity > 0 ? dose.quantity : undefined;
  const timesADay = typeof dose.timesADay === 'number' && dose.timesADay > 0 ? dose.timesADay : undefined;
  const daysInterval = typeof dose.daysInterval === 'number' && dose.daysInterval > 0 ? dose.daysInterval : undefined;
  const refill = typeof dose.refill === 'number' && dose.refill > 0 ? dose.refill : undefined;
  const weight = typeof dose.weight === 'number' && dose.weight > 0 ? dose.weight : undefined;
  // FHIR Duration needs a unit — emit `duration` + `durationUnit` ONLY together
  // (both-or-neither), so a value without a unit (or a unit without a value) can never
  // produce a payload that fails validation downstream (CoS review). The dialog also
  // guards duration-without-unit, so this is a defensive double-check.
  const hasDuration = durationValue !== undefined && !!durationUnit;

  const payload: Extract<CodedOrderPayload, { kind: 'medication' }> = {
    kind: 'medication',
    medicationCode: { code: m.code ?? '', displayName: m.name, ...(system ? { system } : {}) },
    ...(route ? { route: { code: route, displayName: routeDisplay ?? route } } : {}),
    ...(doseUnit ? { doseUnit } : {}),
    ...(frequency ? { frequency } : {}),
    ...(quantity !== undefined ? { quantity } : {}),
    ...(timesADay !== undefined ? { timesADay } : {}),
    ...(daysInterval !== undefined ? { daysInterval } : {}),
    ...(specificDays && specificDays.length > 0 ? { specificDays } : {}),
    ...(startDate ? { startDate } : {}),
    ...(hasDuration ? { duration: durationValue, durationUnit } : {}),
    ...(dispense !== undefined ? { dispense } : {}),
    ...(dispenseUnit ? { dispenseUnit } : {}),
    ...(refill !== undefined ? { refill } : {}),
    ...(dose.substitution != null ? { substitution: dose.substitution } : {}),
    ...(sig ? { sig } : {}),
    ...(vidalAlerts ? { vidalAlerts } : {}),
    ...(note ? { note } : {}),
    ...(weight !== undefined ? { weight } : {}),
    ...(priority ? { priority } : {}),
  };

  return {
    // dxIds empty — the host attaches the target Dx when it appends the order (v2 PROD-1422).
    planOrder: { id: orderId, type: 'medication', label: medicationLabel(m, sig), dxIds: [] },
    payload,
  };
}
